/**
 * DISTRIBUIÇÃO DE REQUISITOS POR DIAS (módulo puro — sem IA, sem Maps).
 *
 * "3 dias e quero praia em 2 deles; no outro quero descansar" é um contrato
 * com três dias, dois temáticos e um leve. O parser é genérico: funciona para
 * qualquer tema (praia, museus, trilhos, termas…) em PT/EN/ES/FR/IT/DE.
 */

import { categoriesInText, resolveCategoryAlias } from "./catalog/taxonomy";

export type DayThemeAllocation = {
  /** Categoria canónica ("beach", "museum"…) ou "rest" para dia leve. */
  theme: string;
  days: number;
  /** Termo original escrito pelo utilizador. */
  term: string;
};

const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

const WORD_NUMBERS: Record<string, number> = {
  um: 1, uma: 1, un: 1, una: 1, one: 1, ein: 1, eine: 1, uno: 1,
  dois: 2, duas: 2, dos: 2, due: 2, deux: 2, two: 2, zwei: 2,
  tres: 3, tre: 3, trois: 3, three: 3, drei: 3,
  quatro: 4, cuatro: 4, quattro: 4, quatre: 4, four: 4, vier: 4,
  cinco: 5, cinque: 5, cinq: 5, five: 5, funf: 5,
};

const toCount = (raw: string | undefined): number | undefined => {
  if (!raw) return undefined;
  const numeric = Number(raw.replace(",", "."));
  if (Number.isFinite(numeric) && numeric > 0) return Math.round(numeric);
  return WORD_NUMBERS[fold(raw)];
};

/** "descansar", "rest", "relax", "chill", "tranquilo". */
const REST_RE =
  /\b(?:descansar|descanso|relaxar|repousar|rest|relax|chill|tranquilo|calmo|erholen|ausruhen|riposare|riposo|reposer|repos|descansando|descanso)\b/u;

/**
 * "praia em 2 deles", "2 dias de praia", "2 days of beach",
 * "2 giorni di mare", "2 Tage Strand".
 */
const ALLOCATION_PATTERNS: RegExp[] = [
  // <tema> em/en N (deles|dias|days)
  /([\p{L}][\p{L} ]{2,24}?)\s+(?:em|en|in|no|nos|na|nas|for|per|pour|für|fur)\s+(\d{1,2}|[a-z]{2,7})\s*(?:deles|delas|dias?|days?|giorni|jours?|tage|d[ií]as)?\b/gu,
  // N dias de <tema>
  /\b(\d{1,2}|[a-z]{2,7})\s*(?:dias?|days?|giorni|jours?|tage|d[ií]as)\s+(?:de|of|di|d'|von|para)\s+([\p{L}][\p{L} ]{2,24})/gu,
];

/**
 * Extrai a distribuição temática dos dias. Devolve lista vazia quando o
 * pedido não distribui nada (o caso normal).
 */
export function parseDayAllocations(text: string, totalDays: number): DayThemeAllocation[] {
  const raw = (text ?? "").replace(/\s+/gu, " ").trim();
  if (!raw || !Number.isFinite(totalDays) || totalDays < 2) return [];
  const folded = fold(raw);
  const out: DayThemeAllocation[] = [];

  const add = (term: string, days: number) => {
    const cleaned = term.replace(/\b(?:quero|queria|fazer|ver|gostava|de|do|da|dos|das|a|o|os|as|e)\b/gu, " ")
      .replace(/\s+/gu, " ")
      .trim();
    if (!cleaned || days <= 0 || days > totalDays) return;
    const theme =
      resolveCategoryAlias(cleaned) ?? categoriesInText(cleaned)[0] ?? (REST_RE.test(cleaned) ? "rest" : undefined);
    if (!theme) return;
    const existing = out.find((entry) => entry.theme === theme);
    if (existing) existing.days = Math.max(existing.days, days);
    else out.push({ theme, days, term: cleaned });
  };

  for (const pattern of ALLOCATION_PATTERNS) {
    pattern.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(folded))) {
      const first = match[1];
      const second = match[2];
      const firstCount = toCount(first);
      if (firstCount) add(second, firstCount);
      else {
        const secondCount = toCount(second);
        if (secondCount) add(first, secondCount);
      }
    }
  }

  const assigned = out.reduce((total, entry) => total + entry.days, 0);
  const remaining = totalDays - assigned;
  // "no outro/nos restantes quero descansar" → os dias que sobram.
  if (
    remaining > 0 &&
    out.length > 0 &&
    /\b(?:no\s+outro|nos\s+outros|nos?\s+restantes|the\s+other|los\s+otros|l'autre|gli\s+altri|am\s+anderen)\b/u.test(folded) &&
    REST_RE.test(folded)
  ) {
    out.push({ theme: "rest", days: remaining, term: "descansar" });
  }

  return out.filter((entry) => entry.days > 0);
}

export type AllocationViolation = { theme: string; expected: number; found: number };

/** Confirma que cada tema ocupa exatamente os dias pedidos. */
export function checkDayAllocations(
  allocations: DayThemeAllocation[],
  dayThemes: Array<string[] | null | undefined>,
): AllocationViolation[] {
  const violations: AllocationViolation[] = [];
  for (const allocation of allocations) {
    const found = dayThemes.filter((themes) => (themes ?? []).includes(allocation.theme)).length;
    if (found !== allocation.days) {
      violations.push({ theme: allocation.theme, expected: allocation.days, found });
    }
  }
  return violations;
}
